<template>
    <operational-area
        :show="{
            operationalArea: !showScreen && show.menu,
            screen: !show.pause,
            iconClose: show.close || show.pause,
            closeButton: show.closeButton
        }"
        :screen="{
            settings: {
                toggles: {
                    request: toggleMessage,
                    searchCall: searchingCall,
                    callFound: requestFound,
                    disabledButton: disableClose
                },
                loading,
                showError: show.close
            }
        }"
        :init="initMenu"
        :closeAction="closeMenu"
    >
        <template #operational__screen__center >
            <div class="operate__standby">
                <pause
                    :actions="{
                        request: newRequest,
                        logout: closeMenu
                    }"
                    @closeModal="show.closeModal = false"
                />
            </div>
        </template>
        <template #operational__area>
            <call-area
                :settings="{
                    data,
                    call,
                    ...( contact && contact.workflow && { workflow: contact.workflow } ),
                    showTimer: show.timer,
                    timeBetweenCalls,
                    blockTypification: block.typification
                }"
                @new-request="newRequest"
                @show-pause="showPause"
                @close-menu="closeMenu"
                @stop-call="activateTimer"
            />
        </template>
    </operational-area>
</template>

<script>
// package
import {
    onUnmounted,
    reactive,
    toRefs,
    computed
} from '@vue/composition-api';

// jssip
import { hangUp, logout } from '@/plugins/jssip';

// sockets
import initConnection from '@/plugins/sockets';

// store
import { useStore, useActions, mapState } from '@/hooks/use-store';

// helpers
import { get, post } from '@/helpers/requests';

// axios
import axios from '@/plugins/axios';

// vue
import Vue from 'vue';

export default {
    components: {
        OperationalArea: () => import( /* webpackChunkName: 'OperationalArea' */ '@/components/bundles/layout/operationalArea/OperationalArea' ),
        Pause: () => import( /* webpackChunkName: 'Pause' */ '@/components/bundles/operate/blocks/Pause' ),
        CallArea: () => import( /* webpackChunkName: 'CallArea' */ '@/components/bundles/layout/operationalArea/blocks/CallArea' )
    },
    props: {
        contact: Object
    },
    setup ( props, { emit }) {
        // definição de vars reactivas.
        const state = reactive({
            socket: null,
            loading: true,
            errorOnRegist: false,
            badRequest: false,
            toggleMessage: false,
            searchingCall: true,
            requestFound: false,
            disableClose: false,
            timeBetweenCalls: 10,
            show: {
                timer: false,
                menu: false,
                rescheduling: false,
                closeButton: true,
                close: false,
                pause: false
            },
            // timerToShowClose: null,
            data: {},
            call: {
                id: 0,
                number: 0
            },
            block: {
                typification: false
            },
            stop: false
        });

        /**
         * Iniciação das configurações do menu.
         * É activado o menu e é feito o registo no
         * freeswitch.
         *
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const initMenu = () => {
            fetchRequest();
            state.toggleMessage = true;
            // iniciação do socket
            const { socket } = initConnection();

            // adicionado globalmente
            state.socket = socket;

            // obtenção do id utilizador autenticado.
            const { getters } = useStore();

            // iniciação da escuta do canal de chamadas activas.
            state.socket.private( `users.${getters.userId}` )
                .listen( '.intercepted.contact.call', ({ data }) => {
                    if ( data.contact_call_id === state.call.id ) {
                        actions( 'resetState', true );
                        state.show.timer = true;
                        state.timeBetweenCalls = 10;
                        state.block.typification = true;

                        // passado 6 segundos faz o reset do block.
                        setTimeout( () => { state.block.typification = false; }, 6000 );
                    }
                });
        };

        /**
         * Fecho do menu operador.
         * É feito a validação se é necessário
         * proceder ao logout do freeswitch.
         *
         * @param {Boolean} logoutIsRequired
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const closeMenu = ( logoutIsRequired = false ) => {
            if ( logoutIsRequired )
                logout();
            if ( state.socket != null )
                state.socket.disconnect();
            axios.clear();
            emit( 'close' );
        };

        // store
        const { actions } = useActions();

        /**
         * Ao fim de 15 segundos é apresentado
         * o X, de forma o utilizador ter
         * possibilidade de sair quando não há chamadas
         * para trabalhar.
         *
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        let resetFetch = 0;
        const resetAndFetch = () => {
            // ao fechar o menu, se ainda tiver a fazero
            // fazer pedidos, é necessário parar.
            if ( state.stop )
                return;

            if ( !state.badRequest )
                state.show.close = true;

            state.badRequest = true;
            resetFetch = setTimeout( fetchRequest, 5000 );
        };

        const payload = mapState({ module: 'storePayload', key: 'payload' });
        // lista de ids de campanhas para ser enviada no pedido de queue

        const queryIds = [];
        if ( payload.value.campaigns ) {
            payload.value.campaigns.forEach( campaign => {
                queryIds.push( `campaignId[]=${campaign}` );
            });
        }
        // array que vai ser enviado no pedido
        const queryStr = queryIds.join( '&' );

        /**
         * Obtenção do pedido de contacto & do
         * ID da chamada.
         * Nesta fase é necessário obter o pedido de contacto,
         * e de seguida submeter o ID desse pedido de forma a gerar
         * a chamada.
         *
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const fetchRequest = async () => {
            // garantir que mostra botão de sair ao procurar chamadas
            state.show.closeButton = true;
            state.requestFound = false;
            // inativa o botão de sair
            state.disableClose = true;
            // timeout de 1s para evitar que o utilizaor saia 1s antes do pedido de chamada
            setTimeout( async () => {
                // pedido a API do pedido de contacto se o tipo de contacto for manual
                if ( props.contact && props.contact.id ) {
                    state.data = await get( `contacts/queue?contactId=${props.contact.id}&contactType=${props.contact.type}` )
                        .then( ({ data }) => data.content.data )
                        .catch( () => { resetAndFetch(); });

                    // reset da prop para no fim da tipificação, fazer uma chamada aleatoria
                    Vue.delete( props.contact, 'id' );
                    // caso ocorra um erro no pedido
                    // do pedido de contacto.
                    if ( !state.data )
                        return;
                    state.show.closeButton = false;
                    state.requestFound = true;
                    // obtenção do número da chamada
                    const getContact = props.contact.value; // obtenção do contacto
                    state.call.number = getContact;
                } else {
                    // pedido a API do pedido de contacto.
                    state.data = await get( `contacts/queue?${queryStr}` )
                        .then( ({ data }) => data.content.data )
                        .catch( () => { resetAndFetch(); });

                    // caso ocorra um erro no pedido
                    // do pedido de contacto. Codigo 404
                    if ( !state.data ) {
                        state.searchingCall = false;
                        state.disableClose = false;
                        if ( state.show.close ) {
                            setTimeout( () => {
                                state.show.close = false;
                                state.badRequest = false;
                                state.searchingCall = true;
                            }, 2000 );
                        }
                        return;
                    }
                    state.show.closeButton = false;
                    state.requestFound = true;
                    // obtenção do número da chamada
                    const getContact = state.data.request.person.contacts.find( contact => contact.type === 'phone'
                    && contact.primary === true ).value; // obtenção do contacto
                    state.call.number = getContact;
                }

                state.timeBetweenCalls = state.data.contactCallSettings?.postCallTimer || 0;

                // limpa o timeout & faz
                // reset dos booleans de
                // apresentação de erro.
                // clearTimeout( state.timerToShowClose );
                state.badRequest = false;
                state.show.close = false;

                actions( 'registerField', { name: 'requestId', value: state.data.id });

                // obtenção do id da chamada
                // é necessário enviar o ID do pedido de contacto.
                const formData = new FormData();
                formData.append( 'contact_request_queue_id', state.data.queueId );
                formData.append( 'campaign_script_id', state.data.script.id );
                state.call.id = await post( `/contacts/${state.data.id}/calls`, formData ).then( ({ data }) => data.content.data.id );

                actions( 'registerField', { name: 'id', value: state.call.id });

                state.loading = false;
                state.show.menu = true;
            }, 1000 );
        };

        onUnmounted( () => {
            // caso saia do menu de Operador
            // enquanto esta a fazer pedidos.
            state.stop = true;
            clearTimeout( resetFetch );
        });

        /**
         * Acabar a pausa entre chamadas.
         * Ao acabar a pausa é feito reset ao contador &
         * é feito um novo pedido de contacto.
         *
         * @param {String} block
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const newRequest = ( block = 'pause' ) => {
            state.show[block] = false;
            state.show.timer = false;
            setTimeout( () => {
                state.loading = true;
                fetchRequest();
            }, 800 );
        };

        const showScreen = computed( () => [ state.loading, state.errorOnRegist, state.show.pause ].find( bool => bool ) );

        /**
         * Toggle do menu para a secção de
         * pausa.
         *
         * @since 1.1.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const showPause = () => {
            state.show.pause = true;
            state.show.menu = false;
        };

        const activateTimer = () => {
            console.log( 'entrou?' );
            state.show.timer = true;
        };

        return {
            ...toRefs( state ),
            initMenu,
            hangUp,
            closeMenu,
            newRequest,
            showScreen,
            showPause,
            activateTimer
        };
    }
};
</script>

<style lang="scss">
    .operate {
        &__standby {
            @include dimensions( 100%, 100% );
        }
    }
</style>